import { Box, Flex, Spinner, Stack, Text, UseDisclosureReturn } from '@chakra-ui/react';
import {
  BaseButton,
  BaseFormDialog,
  FlexFormFields,
  FormComponents,
  useGenericCrudGetQuery,
  useHookForm
} from '@cks-internal/react-base';
import { ControllerRoutes } from '../../../constants/controllerRoutes.ts';
import { ProductListingModel } from './models/ProductListingModel.ts';
import { OrderItem } from '../itemTypes.ts';
import { useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { useProductViewFormFields } from './util/useProductViewFormFields.tsx';
import { useShoppingCartItemStore } from '../shoppingCart/hooks/useShoppingCartStateStore.tsx';
import { PriceTag } from '../../../components/common/PriceTag.tsx';
import { usePriceCalculation } from './util/usePriceCalculation.ts';
import { PackSizeSelector } from './components/PackSizeSelector.tsx';

type ProductViewProps = {
  productId: number;
  disclosure: UseDisclosureReturn;
};

export const ProductViewModal = (props: ProductViewProps) => {
  const { disclosure, productId } = props;
  const productQuery = useGenericCrudGetQuery<ProductListingModel>(productId, ControllerRoutes.ProductListing.Base);

  const { formMethods } = useHookForm<Partial<OrderItem>>({
    ProductStrengthId: 0,
    FlavorId: undefined!,
    ItemComment: '',
    ProductOptionId: undefined!,
    Quantity: 1,
    DrugId: productQuery.data?.Id,
    Price: 0
  });

  const productOptionId = useWatch({
    control: formMethods.control,
    name: 'ProductOptionId'
  });

  const productStrengthId = useWatch({
    control: formMethods.control,
    name: 'ProductStrengthId'
  });

  const quantity = useWatch({
    control: formMethods.control,
    name: 'Quantity'
  });

  const deliveryType = useWatch({
    control: formMethods.control,
    name: 'DeliveryType'
  });

  const deliveryCity = useWatch({
    control: formMethods.control,
    name: 'DeliveryAddress.City'
  });

  useEffect(() => {
    formMethods.setValue('ProductStrengthId', undefined);
    formMethods.setValue('FlavorId', undefined);

    // Set default quantity to minimum quantity (or 1 if not specified)
    if (productQuery.data && productOptionId) {
      const productOption = productQuery.data.ProductOptions.find((po) => po.FormId === productOptionId);
      if (productOption) {
        const minQty = productOption.MinimumQuantity ?? 1;
        formMethods.setValue('Quantity', minQty);
      }
    }
  }, [formMethods, productOptionId, productQuery.data]);

  useEffect(() => {
    if (productQuery.data?.Id) {
      formMethods.setValue('DrugId', productQuery.data.Id);
    }
  }, [productQuery.data?.Id, formMethods]);

  const fields = useProductViewFormFields(formMethods, productQuery.data, productOptionId);

  // Get the selected product option to access pack sizes
  const selectedProductOption = useMemo(() => {
    if (!productQuery.data || !productOptionId) return undefined;
    return productQuery.data.ProductOptions.find((po) => po.FormId === productOptionId);
  }, [productQuery.data, productOptionId]);

  const { price: calculatedPrice, isLoading: isPriceLoading } = usePriceCalculation(
    productOptionId,
    productStrengthId,
    quantity,
    deliveryType,
    deliveryCity
  );

  // Update the price in form when calculation changes
  useEffect(() => {
    if (calculatedPrice !== undefined) {
      formMethods.setValue('Price', calculatedPrice);
    }
  }, [calculatedPrice, formMethods]);

  const productStore = useShoppingCartItemStore();

  if (!productQuery.data) {
    return <></>;
  }

  return (
    <BaseFormDialog
      isLoading={productQuery.isLoading}
      formControlProps={{
        isSubmitting: false,
        buttonConfig: {
          submitButtonProps: { buttonText: 'Add to cart', style: { width: '100%' } }
        }
      }}
      disclosure={disclosure}
      size={'md'}
      header={productQuery.data?.DrugName}
      formMethods={formMethods}
      footerRender={
        <Flex justifyContent={'space-between'} w={'100%'}>
          <Stack direction={{ base: 'column', md: 'row' }} gap='1' align='baseline' justify='space-between'>
            {isPriceLoading ? (
              <Spinner size='sm' />
            ) : (
              calculatedPrice !== undefined && (
                <>
                  <Box fontWeight={'bold'}>Total Cost:</Box>
                  <PriceTag minimumPrice={calculatedPrice} rootProps={{ fontSize: 'xl' }} />
                </>
              )
            )}
          </Stack>

          <BaseButton type={'submit'}>Add To Cart</BaseButton>
        </Flex>
      }
      onSubmit={async (model: Partial<OrderItem>) => {
        productStore.addItem(model as OrderItem);
        disclosure.onClose();
      }}>
      {productQuery.data && (
        <>
          {productQuery.data.DrugDescription && (
            <Text color={'gray.600'} dangerouslySetInnerHTML={{ __html: productQuery.data.DrugDescription }}></Text>
          )}
          <FlexFormFields formFields={fields}></FlexFormFields>
          <FormComponents.FormFieldContainer invalid={false} label={'Quantity'}>
            <PackSizeSelector
              packSizes={selectedProductOption?.PackSizes ?? []}
              minimumQuantity={selectedProductOption?.MinimumQuantity ?? 1}
              value={formMethods.getValues('Quantity') ?? 1}
              onChange={(value) =>
                formMethods.setValue('Quantity', value, {
                  shouldValidate: true,
                  shouldTouch: true,
                  shouldDirty: true
                })
              }
            />
          </FormComponents.FormFieldContainer>
        </>
      )}
    </BaseFormDialog>
  );
};
